/* iceberg-ui */
import {
    Flex,
    Form,
    FormFieldGroup,
    FormInput,
    FormModalBody,
    FormSelect,
    FormTextArea
} from '@auviknetworks/iceberg-ui';

/* MUI */
import { Typography } from '@mui/material';

/* Shared */
import { Notify } from 'application/Shared/Helpers';
import { ModalFormFooterIceberg } from 'application/Shared/Modals/Components/ModalFormFooterIceberg';

/* Hooks */
import {
    usePostUserCustomTableView,
    usePutUserCustomTableView
} from 'hooks/dataAccess/UserCustomTableView/userCustomTableView';

/*  React  */
import React, { useState } from 'react';
import { Resolver, ResolverResult } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

/* Internal Components */
import { useManagedTableV2Context } from '../../ManagedTableV2ContextProvider';
import {
    AccessType,
    CustomTableContext,
    CustomTableView,
    ShowFlyoutMenuActionEnum
} from '../../types';
import { useTableStateFromURL } from '../../utils';

interface CustomViewFormValues {
    accessType: AccessType;
    description: string;
    displayName: string;
}

export const SaveEditTableView = <T,>({
    'data-testid': dataTestId = 'saveEditTableView'
}: {
    /** The data test id for this table flyout component. */
    'data-testid'?: string;
}) => {
    const { t } = useTranslation();
    const {
        currentTableView,
        customTableViews,
        refetchCustomTableViews,
        setCurrentTableView,
        setShowFlyoutMenu,
        showFlyoutMenu,
        tableName
    } = useManagedTableV2Context<T>() as CustomTableContext<T> & {
        currentTableView: CustomTableView;
    };
    const [helperText, setHelperText] = useState<string>(
        t(
            `TableViews.flyout.form.helperText.${
                currentTableView.accessType || AccessType.personal
            }`
        )
    );

    const stateFromURL = useTableStateFromURL(tableName);

    const { postUserCustomTableView } = usePostUserCustomTableView(t);
    const { putUserCustomTableView } = usePutUserCustomTableView(t);

    const onSubmit = async ({
        accessType,
        description,
        displayName
    }: CustomViewFormValues): Promise<void> => {
        let result;

        const trimmeDisplayName = displayName?.trim();
        const trimmedDescription = description?.trim();
        if (showFlyoutMenu === ShowFlyoutMenuActionEnum.save) {
            result = await postUserCustomTableView({
                accessType,
                config: stateFromURL,
                description: trimmedDescription,
                displayName: trimmeDisplayName,
                targetTable: tableName
            });
        }
        if (showFlyoutMenu === ShowFlyoutMenuActionEnum.edit) {
            result = await putUserCustomTableView({
                accessType,
                config: stateFromURL,
                description: trimmedDescription,
                displayName: trimmeDisplayName,
                id: currentTableView.id,
                targetTable: tableName
            });
        }

        if (result?.data) {
            Notify.s(
                t(`TableViews.userCustomTableView.${showFlyoutMenu}.success`, {
                    displayName: trimmeDisplayName
                })
            );
            if (refetchCustomTableViews) {
                refetchCustomTableViews();
            }
            setCurrentTableView({
                accessType,
                description: trimmedDescription,
                displayName: trimmeDisplayName,
                id: result.data,
                state: stateFromURL
            });
            setShowFlyoutMenu(ShowFlyoutMenuActionEnum.hide);
        } else if (result?.error) {
            Notify.e(
                t(`TableViews.userCustomTableView.${showFlyoutMenu}.error`, {
                    displayName: trimmeDisplayName
                })
            );
        }
    };

    const currentCustomTableNames = customTableViews?.reduce(
        (acc: string[], view: CustomTableView) => {
            if (
                !(
                    showFlyoutMenu === ShowFlyoutMenuActionEnum.edit &&
                    view.displayName === currentTableView.displayName
                )
            ) {
                acc.push(view.displayName.toLowerCase());
            }
            return acc;
        },
        []
    );

    const validate: Resolver<CustomViewFormValues> = async (
        values: CustomViewFormValues
    ) => {
        const results: ResolverResult<CustomViewFormValues> = {
            errors: {},
            values: {}
        };

        if (!values.displayName?.trim()) {
            results.errors.displayName = {
                message: t('TableViews.flyout.form.fieldIsRequired'),
                type: 'required'
            };
        }

        if (
            currentCustomTableNames?.includes(
                values.displayName?.trim().toLowerCase()
            )
        ) {
            results.errors.displayName = {
                message: t('TableViews.flyout.form.nameInUse'),
                type: 'required'
            };
        }

        // If there are no errors, return the values.
        if (!Object.keys(results.errors).length) results.values = values;

        return results;
    };

    return (
        <Flex flexDirection="column" data-testid={dataTestId} width="100%">
            <Typography p="22px">
                {t(`TableViews.flyout.${showFlyoutMenu}.subtitle`)}
            </Typography>

            <Form<CustomViewFormValues>
                defaultValues={{
                    accessType:
                        currentTableView.accessType || AccessType.personal,
                    description: currentTableView.description,
                    displayName: currentTableView.displayName
                }}
                onSubmit={async ({ accessType, description, displayName }) => {
                    await onSubmit({
                        accessType,
                        description,
                        displayName
                    });
                }}
                sx={{ flex: 1 }}
                validate={validate}
            >
                <Flex
                    flexDirection="column"
                    height="100%"
                    justifyContent="space-between"
                >
                    <FormModalBody>
                        <FormFieldGroup>
                            <FormInput
                                label={t('TableViews.flyout.form.displayName')}
                                name="displayName"
                                inputType="text"
                                required
                            />
                            <FormSelect
                                label={t('TableViews.flyout.form.accessType')}
                                helperText={helperText}
                                name="accessType"
                                onChange={(option: {
                                    label: string;
                                    value: string;
                                }) => {
                                    setHelperText(
                                        t(
                                            `TableViews.flyout.form.helperText.${option.value}`
                                        )
                                    );
                                }}
                                options={[
                                    {
                                        label: t(
                                            `TableViews.flyout.form.accessType.${AccessType.personal}`
                                        ),
                                        value: AccessType.personal
                                    },
                                    {
                                        label: t(
                                            `TableViews.flyout.form.accessType.${AccessType.tenant}`
                                        ),
                                        value: AccessType.tenant
                                    }
                                ]}
                                width="100%"
                            />
                            <FormTextArea
                                label={t('TableViews.flyout.form.description')}
                                name="description"
                            />
                        </FormFieldGroup>
                    </FormModalBody>
                    <ModalFormFooterIceberg
                        data-trackid={`${showFlyoutMenu}CustomView_${tableName}`}
                        onClose={() => {
                            setShowFlyoutMenu(ShowFlyoutMenuActionEnum.hide);
                        }}
                        onCloseBtnText={t('Common.close')}
                        onSubmitBtnText={t('Common.save')}
                    />
                </Flex>
            </Form>
        </Flex>
    );
};
